import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../SEO';
import { buildStructuredData, getAbsoluteUrl, getKeywords, getRouteSeo, siteSeo } from '../../seo/seoUtils';
import './Rockets.css';
import gslv from '../../assets/gslv.png';
import pslv from '../../assets/pslv.png';

const vehicles = [
  {
    name: 'RPSLV',
    image: pslv,
    label: 'Light launch vehicle',
    description:
      'A reliable vehicle optimized for small-satellite launches into low Earth orbit and sun-synchronous missions.',
    specs: [
      { label: 'Payload', value: '450 kg class' },
      { label: 'Primary orbits', value: 'LEO and SSO' },
      { label: 'Mission style', value: 'Responsive launch' }
    ],
    tags: ['CubeSat deployments', 'Research payloads', 'Lower-cost access']
  },
  {
    name: 'RMLV',
    image: gslv,
    label: 'Medium launch vehicle',
    description:
      'An advanced launch platform for heavier payloads, complex orbital insertion, and ambitious exploration programs.',
    specs: [
      { label: 'Payload', value: 'Medium-lift class' },
      { label: 'Primary orbits', value: 'LEO to transfer missions' },
      { label: 'Mission style', value: 'Scaled deployment' }
    ],
    tags: ['Commercial constellations', 'Deep tech missions', 'Multi-stage precision']
  }
];

const introVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
  }
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.14, delayChildren: 0.05 }
  }
};

const cardVariants = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  }
};

const Rockets = ({ enableSEO = true }) => {
  const seo = getRouteSeo('/rockets');
  const HeadingTag = enableSEO ? 'h1' : 'h2';

  return (
    <>
      {enableSEO && (
        <SEO
          title={seo.title}
          description={seo.description}
          keywords={getKeywords(seo)}
          url={getAbsoluteUrl('/rockets')}
          image={siteSeo.image}
          imageAlt={siteSeo.imageAlt}
          structuredData={buildStructuredData('/rockets')}
        />
      )}

      <section id="rockets" className="rockets-section">
        <div className="rockets-section__container">
          <motion.div
            className="rockets-section__intro"
            variants={introVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.span className="section-kicker" variants={fadeUp}>Launch vehicles</motion.span>
            <HeadingTag className="section-heading">
              <motion.span style={{ display: 'block' }} variants={fadeUp}>
                Vehicles engineered for <span>precision, speed, and flexibility</span>.
              </motion.span>
            </HeadingTag>
            <motion.p className="section-copy" variants={fadeUp}>
              Our launch concepts are shaped to support everything from agile CubeSat deployment to
              scaled missions requiring higher payload capacity and tighter mission control.
            </motion.p>
          </motion.div>

          <motion.div
            className="rockets-section__grid"
            variants={gridVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {vehicles.map((vehicle) => (
              <motion.article
                key={vehicle.name}
                className="rockets-section__card glass-card"
                variants={cardVariants}
              >
                <div className="rockets-section__media">
                  <img
                    src={vehicle.image}
                    alt={`${vehicle.name} launch vehicle`}
                    loading="lazy"
                    width="640"
                    height="400"
                  />
                </div>

                <div className="rockets-section__body">
                  <span className="rockets-section__label">{vehicle.label}</span>
                  <h3>{vehicle.name}</h3>
                  <p>{vehicle.description}</p>

                  <div className="rockets-section__specs">
                    {vehicle.specs.map((spec) => (
                      <div key={spec.label} className="rockets-section__spec">
                        <span>{spec.label}</span>
                        <strong>{spec.value}</strong>
                      </div>
                    ))}
                  </div>

                  <div className="rockets-section__tags">
                    {vehicle.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Rockets;
